import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Megaphone, BookOpen, Network, UserPlus, Search, RefreshCw, ThumbsUp,
  ChevronRight, ChevronDown, Plus, ArrowLeft, LifeBuoy,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { EmptyState, CardSkeleton } from './common/States';
import {
  hrOpsAPI, knowledgeBaseAPI, directoryAPI, referralAPI,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SupportCenter from './SupportCenter';
import HelpArticles from './HelpArticles';
import { getErrorMessage } from '../lib/utils';

const KB_AUTHOR_ROLES = ['workspace_admin', 'admin', 'hr_admin', 'hr_manager'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  hired: 'default',
  selected: 'default',
  rejected: 'destructive',
  screening: 'secondary',
  submitted: 'outline',
}[s] || 'secondary');

const Section = ({ icon: Icon, title, description, action, loading, isEmpty, emptyText, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent>
      {loading && <CardSkeleton rows={4} />}
      {!loading && isEmpty && <EmptyState compact title={emptyText} />}
      {!loading && !isEmpty && children}
    </CardContent>
  </Card>
);

/** One node and, when expanded, its reports.
 *
 *  The API caps depth, so a node at the boundary reports a direct_report_count
 *  it has no children for. Rather than dead-end there, that node offers to
 *  re-root the chart on itself — which is what root_employee_id is for.
 */
const OrgNode = ({ node, level, onDrill }) => {
  const [open, setOpen] = useState(level < 2);
  const reports = node.reports || [];
  const hasReports = reports.length > 0;
  const truncated = !hasReports && node.direct_report_count > 0;
  return (
    <div className={level ? 'border-l border-border pl-4 ml-2' : ''}>
      <div className="flex items-center gap-2 py-1.5">
        <button
          type="button"
          onClick={() => hasReports && setOpen(!open)}
          className={`flex h-5 w-5 items-center justify-center rounded ${hasReports ? 'hover:bg-muted' : 'invisible'}`}
          aria-label={open ? 'Collapse' : 'Expand'}
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{node.name || node.employee_id}</div>
          <div className="text-xs text-muted-foreground truncate">
            {[node.designation, node.department].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        {truncated ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => onDrill(node.employee_id)}
          >
            Show {node.direct_report_count} reports
          </Button>
        ) : node.direct_report_count > 0 && (
          <Badge variant="outline" className="ml-auto shrink-0">
            {node.direct_report_count}
          </Badge>
        )}
      </div>
      {open && hasReports && (
        <div>
          {reports.map((child) => (
            <OrgNode key={child.employee_id} node={child} level={level + 1} onDrill={onDrill} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The shared-workplace surfaces: what the company is telling you, where the
 * answers live, who everyone is, and how to bring someone in.
 */
const WorkplaceModule = ({ currentTab = 'announcements', onNavigate }) => {
  const { user } = useAuth();
  const canAuthor = KB_AUTHOR_ROLES.includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [kbQuery, setKbQuery] = useState('');
  const [directory, setDirectory] = useState([]);
  const [dirQuery, setDirQuery] = useState('');
  const [chart, setChart] = useState(null);
  const [chartRoot, setChartRoot] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralDialog, setReferralDialog] = useState(false);
  const [kbDialog, setKbDialog] = useState(false);
  const [openJobs, setOpenJobs] = useState([]);
  const [referralForm, setReferralForm] = useState({
    job_posting_id: '', first_name: '', last_name: '', email: '', phone: '', notes: '',
  });
  const [kbForm, setKbForm] = useState({ title: '', body: '', category: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      hrOpsAPI.listAnnouncements(),
      knowledgeBaseAPI.list(),
      directoryAPI.list({ limit: 200 }),
      directoryAPI.orgChart({ depth: 4 }),
      referralAPI.list(),
      referralAPI.listOpenJobs(),
    ]);
    const value = (r, fallback) => (r.status === 'fulfilled' ? r.value.data : fallback);
    setAnnouncements(value(results[0], []));
    setArticles(value(results[1], []));
    setDirectory(value(results[2], { results: [] }).results || []);
    setChart(value(results[3], null));
    setReferrals(value(results[4], []));
    setOpenJobs(value(results[5], []));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const searchKb = async () => {
    try {
      const { data } = await knowledgeBaseAPI.list(kbQuery ? { q: kbQuery } : undefined);
      setArticles(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Search failed'));
    }
  };

  const openArticle = async (slug) => {
    try {
      const { data } = await knowledgeBaseAPI.get(slug);
      setArticle(data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not open article'));
    }
  };

  const markHelpful = async (slug) => {
    try {
      const { data } = await knowledgeBaseAPI.markHelpful(slug);
      setArticle((prev) => (prev ? { ...prev, helpful_count: data.helpful_count } : prev));
      toast.success('Thanks — noted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not record that'));
    }
  };

  const createArticle = async () => {
    if (!kbForm.title.trim() || !kbForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    try {
      await knowledgeBaseAPI.create(kbForm);
      toast.success('Article published');
      setKbDialog(false);
      setKbForm({ title: '', body: '', category: '' });
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not publish'));
    }
  };

  const submitReferral = async () => {
    const { job_posting_id: jobId, first_name: first, last_name: last, email } = referralForm;
    if (!jobId || !first.trim() || !last.trim() || !email.trim()) {
      toast.error('Role, name and email are required');
      return;
    }
    try {
      await referralAPI.create({ ...referralForm, job_posting_id: Number(jobId) });
      toast.success('Referral submitted');
      setReferralDialog(false);
      setReferralForm({
        job_posting_id: '', first_name: '', last_name: '', email: '', phone: '', notes: '',
      });
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not submit referral'));
    }
  };

  const jobById = useMemo(
    () => Object.fromEntries(openJobs.map((j) => [j.id, j])),
    [openJobs],
  );

  const drillTo = useCallback(async (employeeId) => {
    try {
      const { data } = await directoryAPI.orgChart(
        employeeId ? { root_employee_id: employeeId, depth: 4 } : { depth: 4 },
      );
      setChart(data);
      setChartRoot(employeeId || null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not load that part of the chart'));
    }
  }, []);

  const filteredDirectory = useMemo(() => {
    const needle = dirQuery.trim().toLowerCase();
    if (!needle) return directory;
    return directory.filter((p) =>
      [p.name, p.designation, p.department, p.work_email]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(needle)));
  }, [directory, dirQuery]);

  if (currentTab === 'announcements') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Megaphone}
          title="Announcements"
          description="What the organisation is telling everyone."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!announcements.length}
          emptyText="Nothing announced right now."
        >
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.id} className="rounded-lg border border-border p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="font-medium">{a.title}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    {a.category && <Badge variant="outline">{titleize(a.category)}</Badge>}
                    {a.published_at && (
                      <span className="text-xs text-muted-foreground">
                        {String(a.published_at).slice(0, 10)}
                      </span>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{a.body}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>
    );
  }

  if (currentTab === 'support') {
    // Sibling tabs live under the same `workplace-` prefix, so moving between
    // them is a prefix away rather than a route.
    return (
      <SupportCenter
        onNavigate={onNavigate ? (tab) => onNavigate(tab === 'helpdesk' ? 'helpdesk' : `workplace-${tab}`) : undefined}
      />
    );
  }

  if (currentTab === 'help') {
    return (
      <HelpArticles footerNote="Looking for your own organisation's policies? Those live in the Knowledge Base." />
    );
  }

  if (currentTab === 'kb') {
    if (article) {
      return (
        <div className="space-y-6 animate-fade-in">
          <Button variant="ghost" size="sm" onClick={() => setArticle(null)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> All articles
          </Button>
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle>{article.title}</CardTitle>
              <div className="flex items-center gap-2">
                {article.category && <Badge variant="outline">{article.category}</Badge>}
                <span className="text-xs text-muted-foreground">
                  {article.view_count} views · {article.helpful_count} found this helpful
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm whitespace-pre-line leading-relaxed">{article.body}</p>
              <Button variant="outline" size="sm" onClick={() => markHelpful(article.slug)}>
                <ThumbsUp className="h-4 w-4 mr-1" /> This helped
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={BookOpen}
          title="Knowledge base"
          description="Answers to the questions that do not need a ticket."
          action={canAuthor && (
            <Button size="sm" onClick={() => setKbDialog(true)}>
              <Plus className="h-4 w-4 mr-1" /> New article
            </Button>
          )}
          loading={loading}
          isEmpty={!articles.length}
          emptyText="No articles published yet."
        >
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search articles"
                value={kbQuery}
                onChange={(e) => setKbQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchKb()}
              />
              <Button variant="outline" onClick={searchKb}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {articles.map((a) => (
                <button
                  key={a.slug}
                  type="button"
                  onClick={() => openArticle(a.slug)}
                  className="w-full rounded-lg border border-border p-3 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-medium text-sm">{a.title}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {!a.is_published && <Badge variant="outline">Draft</Badge>}
                      {a.category && <Badge variant="secondary">{a.category}</Badge>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Section>

        <Dialog open={kbDialog} onOpenChange={setKbDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>New article</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={kbForm.title}
                  onChange={(e) => setKbForm({ ...kbForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  placeholder="Leave, Payroll, IT…"
                  value={kbForm.category}
                  onChange={(e) => setKbForm({ ...kbForm, category: e.target.value })}
                />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea
                  rows={8}
                  value={kbForm.body}
                  onChange={(e) => setKbForm({ ...kbForm, body: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setKbDialog(false)}>Cancel</Button>
              <Button onClick={createArticle}>Publish</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (currentTab === 'directory') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Search}
          title="People"
          description="Everyone you are allowed to see, at the detail you are allowed to see."
          loading={loading}
          isEmpty={!directory.length}
          emptyText="No colleagues to show."
        >
          <div className="space-y-4">
            <Input
              placeholder="Search by name, role or team"
              value={dirQuery}
              onChange={(e) => setDirQuery(e.target.value)}
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Today</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDirectory.map((p) => (
                  <TableRow key={p.employee_id}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-muted-foreground">{p.designation || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{p.department || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{p.work_email || '—'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{titleize(p.presence || 'unknown')}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      </div>
    );
  }

  if (currentTab === 'org-chart') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Network}
          title="Org chart"
          description="Who reports to whom, from the top down."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!(chart?.nodes || []).length}
          emptyText="No reporting hierarchy to show."
        >
          <div className="space-y-1">
            {chartRoot && (
              <Button variant="ghost" size="sm" className="mb-2" onClick={() => drillTo(null)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Whole organisation
              </Button>
            )}
            {(chart?.nodes || []).map((n) => (
              <OrgNode key={n.employee_id} node={n} level={0} onDrill={drillTo} />
            ))}
          </div>
        </Section>
      </div>
    );
  }

  // referrals
  return (
    <div className="space-y-6 animate-fade-in">
      <Section
        icon={UserPlus}
        title="Referrals"
        description="People you have put forward, and where they got to."
        action={(
          <Button size="sm" onClick={() => setReferralDialog(true)}>
            <Plus className="h-4 w-4 mr-1" /> Refer someone
          </Button>
        )}
        loading={loading}
        isEmpty={!referrals.length}
        emptyText="You have not referred anyone yet."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Role</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Referred on</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {referrals.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">
                  {jobById[r.job_posting_id]?.title || `Role #${r.job_posting_id}`}
                </TableCell>
                <TableCell className="text-muted-foreground">{r.notes || '—'}</TableCell>
                <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                <TableCell className="text-muted-foreground">
                  {String(r.created_at || '').slice(0, 10) || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <Dialog open={referralDialog} onOpenChange={setReferralDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Refer a candidate</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Open role</Label>
              <Select
                value={referralForm.job_posting_id}
                onValueChange={(v) => setReferralForm({ ...referralForm, job_posting_id: v })}
              >
                <SelectTrigger><SelectValue placeholder="Select an opening" /></SelectTrigger>
                <SelectContent>
                  {openJobs.map((j) => (
                    <SelectItem key={j.id} value={String(j.id)}>
                      {j.title}{j.department ? ` — ${j.department}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input
                  value={referralForm.first_name}
                  onChange={(e) => setReferralForm({ ...referralForm, first_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Last name</Label>
                <Input
                  value={referralForm.last_name}
                  onChange={(e) => setReferralForm({ ...referralForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={referralForm.email}
                  onChange={(e) => setReferralForm({ ...referralForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={referralForm.phone}
                  onChange={(e) => setReferralForm({ ...referralForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Why they would be a fit</Label>
              <Textarea
                rows={4}
                value={referralForm.notes}
                onChange={(e) => setReferralForm({ ...referralForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReferralDialog(false)}>Cancel</Button>
            <Button onClick={submitReferral}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkplaceModule;
